import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FaCertificate, FaUpload, FaCheckCircle, FaClock, FaTimesCircle, FaInfoCircle, FaCoins } from 'react-icons/fa';
import { certificationAPI } from '../../services/api';

const Certification = () => {
  const { user } = useSelector((state) => state.auth);
  
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [certificationType, setCertificationType] = useState('identity');
  const [document, setDocument] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchHistory();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await certificationAPI.getStatus();
      setStatus(response.data.data);
    } catch (error) {
      console.error('Error fetching certification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await certificationAPI.getHistory();
      setHistory(response.data.data || []);
    } catch (error) {
      console.error('Error fetching certification history:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert('Le fichier ne doit pas dépasser 10MB');
        return;
      }
      setDocument(file);
      setDocumentPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!document) {
      alert('Veuillez sélectionner un document');
      return;
    }

    setSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('certification_type', certificationType);
      formData.append('document', document);

      const response = await certificationAPI.request(formData);
      
      alert(response.data.message || 'Demande soumise avec succès!');
      
      // Reset form
      setDocument(null);
      setDocumentPreview(null);
      setCertificationType('identity');
      
      // Refresh status and history
      await fetchStatus();
      await fetchHistory();
      
    } catch (error) {
      console.error('Error submitting certification:', error);
      alert(error.response?.data?.message || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (certStatus) => {
    switch (certStatus) {
      case 'approved':
        return <FaCheckCircle className="text-success" size={48} />;
      case 'pending':
        return <FaClock className="text-warning" size={48} />;
      case 'rejected':
        return <FaTimesCircle className="text-danger" size={48} />;
      default:
        return <FaCertificate className="text-primary" size={48} />;
    }
  };

  const getStatusText = (certStatus) => {
    switch (certStatus) {
      case 'approved':
        return 'Compte certifié';
      case 'pending':
        return 'Demande en attente';
      case 'rejected':
        return 'Demande rejetée';
      default:
        return 'Non certifié';
    }
  };

  const getStatusDescription = (certStatus) => {
    switch (certStatus) {
      case 'approved':
        return 'Votre compte est certifié. Vous bénéficiez d\'un badge de certification visible sur votre profil.';
      case 'pending':
        return 'Votre demande de certification est en cours d\'examen. Vous recevrez une notification dès qu\'elle sera traitée.';
      case 'rejected':
        return 'Votre demande a été rejetée. Vous pouvez soumettre une nouvelle demande avec un document valide.';
      default:
        return 'Certifiez votre compte pour gagner en crédibilité et obtenir un badge spécial sur votre profil.';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="certification-page min-vh-100" style={{ backgroundColor: 'var(--lova-bg-secondary)' }}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Header */}
            <div className="text-center mb-5">
              <FaCertificate className="text-primary mb-3" size={64} />
              <h1 className="h2 mb-2">Certification du Compte</h1>
              <p className="text-muted">Obtenez votre badge de certification</p>
            </div>

            {/* Status Card */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-4">
                <div className="d-flex align-items-center">
                  <div className="me-4">
                    {getStatusIcon(status?.is_certified ? 'approved' : status?.has_pending_request ? 'pending' : null)}
                  </div>
                  <div>
                    <h3 className="h5 mb-1">
                      {status?.is_certified ? 'Compte Certifié' : getStatusText(history[0]?.status)}
                    </h3>
                    <p className="text-muted mb-0">
                      {status?.is_certified 
                        ? 'Votre compte est certifié avec succès!'
                        : getStatusDescription(history[0]?.status)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Form - Only show if not certified and no pending request */}
            {!status?.is_certified && !status?.has_pending_request && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h2 className="h5 mb-0">Nouvelle Demande</h2>
                </div>
                <div className="card-body p-4">
                  {/* Cost Info */}
                  <div className="alert alert-info d-flex align-items-center mb-4">
                    <FaCoins className="me-2" />
                    <div>
                      <strong>Coût: {status?.cost} Diamant</strong>
                      <br />
                      <small>Solde actuel: {status?.current_balance} Diamant</small>
                    </div>
                  </div>

                  {!status?.has_enough && (
                    <div className="alert alert-warning">
                      <FaInfoCircle className="me-2" />
                      Solde insuffisant. Veuillez <a href="/wallet">recharger votre portefeuille</a>.
                    </div>
                  )}

                  {status?.has_enough && (
                    <form onSubmit={handleSubmit}>
                      {/* Certification Type */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold">Type de Certification</label>
                        <select 
                          className="form-select"
                          value={certificationType}
                          onChange={(e) => setCertificationType(e.target.value)}
                          required
                        >
                          <option value="identity">Pièce d'identité (CNI, Passeport)</option>
                          <option value="education">Diplôme ou Certificat d'études</option>
                          <option value="professional">Certification professionnelle</option>
                        </select>
                        <div className="form-text">
                          Choisissez le type de document que vous souhaitez soumettre pour certification.
                        </div>
                      </div>

                      {/* Document Upload */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold">Document</label>
                        <div className="border rounded-3 p-4 text-center" style={{ borderStyle: 'dashed' }}>
                          {documentPreview ? (
                            <div className="mb-3">
                              {document?.type?.startsWith('image/') ? (
                                <img 
                                  src={documentPreview} 
                                  alt="Preview" 
                                  className="img-fluid rounded" 
                                  style={{ maxHeight: '200px' }}
                                />
                              ) : (
                                <div className="p-3 bg-light rounded">
                                  <FaUpload size={48} className="text-muted mb-2" />
                                  <p className="mb-0">{document?.name}</p>
                                </div>
                              )}
                              <button 
                                type="button"
                                className="btn btn-sm btn-outline-danger mt-2"
                                onClick={() => {
                                  setDocument(null);
                                  setDocumentPreview(null);
                                }}
                              >
                                Supprimer
                              </button>
                            </div>
                          ) : (
                            <div>
                              <FaUpload size={48} className="text-muted mb-3" />
                              <p className="text-muted mb-2">Glissez-déposez un fichier ou cliquez pour sélectionner</p>
                              <p className="text-muted small mb-3">PDF, JPG, PNG (max 10MB)</p>
                              <input
                                type="file"
                                className="form-control"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={handleFileChange}
                                required
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Submit Button */}
                      <button 
                        type="submit" 
                        className="btn btn-primary w-100"
                        disabled={submitting || !document}
                      >
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Soumission en cours...
                          </>
                        ) : (
                          <>
                            <FaCertificate className="me-2" />
                            Soumettre la demande ({status?.cost} Diamant)
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            )}

            {/* History */}
            {history.length > 0 && (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3">
                  <h2 className="h5 mb-0">Historique des Demandes</h2>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Statut</th>
                          <th>Document</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((cert) => (
                          <tr key={cert.id}>
                            <td>{new Date(cert.created_at).toLocaleDateString('fr-FR')}</td>
                            <td>
                              <span className="badge bg-info">
                                {cert.certification_type || cert.documents?.type}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${
                                cert.status === 'approved' ? 'bg-success' :
                                cert.status === 'pending' ? 'bg-warning text-dark' :
                                'bg-danger'
                              }`}>
                                {cert.status === 'approved' ? 'Approuvée' :
                                 cert.status === 'pending' ? 'En attente' :
                                 'Rejetée'}
                              </span>
                            </td>
                            <td>
                              <button 
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => window.open(`/api/v1/certifications/${cert.id}/document`, '_blank')}
                              >
                                Voir
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Certification;
